import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, BadgeCheck, ShoppingBag } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { formatBRL } from "../api";

export function ProductModal() {
  const { selected, setSelected, addToCart, setCartOpen } = useStore();
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => { setSize(null); setColor(null); setActiveImg(0); }, [selected]);

  const gallery = selected ? [...new Set([selected.image, ...(selected.images || [])])] : [];
  const hasColors = (selected?.colors || []).length > 0;
  const canAdd = size && (!hasColors || color);

  return (
    <AnimatePresence>
      {selected && (
        <motion.div
          data-testid="vult-product-detail-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative grid max-h-[92vh] w-full max-w-4xl grid-cols-1 overflow-y-auto rounded-t-3xl bg-white sm:rounded-3xl md:grid-cols-2"
          >
            <button
              data-testid="vult-product-detail-close-btn"
              onClick={() => setSelected(null)}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md transition-colors hover:bg-vult-red hover:text-slate-900"
            >
              <X size={18} />
            </button>
            <div className="relative bg-[#F8F9FA]">
              <img
                data-testid="vult-product-detail-main-img"
                src={gallery[activeImg] || selected.image}
                alt={selected.name}
                className="h-64 w-full object-cover md:h-full md:min-h-[420px]"
              />
              {selected.badge && (
                <span className={`absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${selected.badge_color || "bg-yellow-400 text-slate-900"}`}>
                  {selected.badge}
                </span>
              )}
              {gallery.length > 1 && (
                <div data-testid="vult-product-detail-gallery" className="absolute inset-x-0 bottom-3 flex justify-center gap-2">
                  {gallery.map((url, i) => (
                    <button
                      key={i}
                      data-testid={`vult-product-detail-thumb-${i}`}
                      onClick={() => setActiveImg(i)}
                      className={`h-14 w-14 overflow-hidden rounded-xl border-2 bg-white transition-all ${activeImg === i ? "border-slate-900 shadow-[2px_2px_0_#FFD600]" : "border-white opacity-70 hover:opacity-100"}`}
                    >
                      <img src={url} alt={`foto ${i + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col p-6 sm:p-8">
              <div className="font-mono text-xs font-bold uppercase tracking-widest text-vult-blue">{selected.brand} • {selected.category}{selected.gender && selected.gender !== "Unissex" ? ` • ${selected.gender}` : ""}</div>
              <h3 className="mt-2 font-display text-3xl font-black uppercase leading-none text-slate-900">{selected.name}</h3>
              <div className="mt-2 flex items-center gap-1.5 font-mono text-xs font-bold text-slate-600">
                <Star size={13} className="fill-amber-400 text-amber-400" /> {selected.rating}
                <span className="font-medium text-slate-400">— {selected.reviews_count} avaliações</span>
              </div>
              <div className="mt-4 flex items-baseline gap-3">
                <span className="font-display text-4xl font-black text-slate-900">{formatBRL(selected.price)}</span>
                {selected.old_price && <span className="text-base font-medium text-slate-400 line-through">{formatBRL(selected.old_price)}</span>}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{selected.description}</p>

              {hasColors && (
                <div className="mt-6">
                  <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    Cor {color ? `— ${color} selecionada` : ""}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selected.colors.map((c) => (
                      <button
                        key={c}
                        data-testid={`vult-product-detail-color-btn-${c.toLowerCase().replace(/\s/g, "-")}`}
                        onClick={() => setColor(c)}
                        className={`rounded-full px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-all ${
                          color === c ? "bg-slate-900 text-white shadow-[3px_3px_0_#FFD600]" : "border border-slate-300 text-slate-700 hover:border-slate-900"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Tamanho BR {size ? `— ${size} selecionado` : ""}
                </div>
                <div className="flex flex-wrap gap-2">
                  {selected.sizes.map((s) => (
                    <button
                      key={s}
                      data-testid={`vult-product-detail-size-btn-${s}`}
                      onClick={() => setSize(s)}
                      className={`h-11 w-11 rounded-xl font-mono text-sm font-bold transition-all ${
                        size === s ? "bg-slate-900 text-white shadow-[3px_3px_0_#FFD600]" : "border border-slate-300 text-slate-700 hover:border-slate-900"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="vult-product-detail-add-cart-btn"
                onClick={() => {
                  if (!canAdd) return;
                  addToCart(selected, size, color);
                  setSelected(null);
                  setCartOpen(true);
                }}
                disabled={!canAdd}
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-vult-red py-4 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 transition-all enabled:hover:bg-slate-900 enabled:hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ShoppingBag size={16} /> {canAdd ? "Adicionar ao carrinho" : hasColors && !color ? "Selecione tamanho e cor" : "Selecione um tamanho"}
              </button>
              <div className="mt-4 flex items-center justify-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-600">
                <BadgeCheck size={13} /> Qualidade altíssima verificada pela Vult
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
